/**
 * School Service
 * Handles API operations for schools
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS } from '../api';
import { SchoolRead } from '../models/school.types';

@Injectable({
  providedIn: 'root',
})
export class SchoolService {
  private readonly http = inject(HttpClient);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get all schools
   * @param params Optional query parameters (e.g., { deleted: false })
   */
  getSchools(params?: { deleted?: boolean }): Observable<SchoolRead[]> {
    this.loading.set(true);
    this.error.set(null);

    const queryParams: any = {};
    if (params?.deleted !== undefined) {
      queryParams.deleted = params.deleted;
    }

    return this.http
      .get<SchoolRead[]>(APP_SCHEMA_ENDPOINTS.schools.list, {
        params: queryParams,
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get school by ID
   */
  getSchool(schoolId: number): Observable<SchoolRead> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<SchoolRead>(APP_SCHEMA_ENDPOINTS.schools.getById(schoolId), {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Create authorization headers
   */
  private createHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    });
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    this.loading.set(false);

    const errorMessage =
      error?.error?.message || error?.message || 'An error occurred while fetching data';

    this.error.set(errorMessage);
    console.error('SchoolService error:', error);

    return throwError(() => new Error(errorMessage));
  }
}
